// Pure client-side pre-validation for GritClaw file attachments. Kept out of the
// API client (and out of React) so it is trivially unit-testable and not affected
// by module mocks. The gateway remains the authority — this only fails fast on the
// obviously-wrong (over the size cap or an unsupported type) before any upload.

/** Hard size cap mirrored by the gateway (413 over this). */
export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024; // 25 MB

/** Max staged files forwarded on a single turn (gateway caps this too). */
export const MAX_ATTACHMENTS_PER_MESSAGE = 5;

/** Allowed MIME types: images (png/jpeg/webp/gif) + pdf/csv/txt/xlsx/docx. */
const ALLOWED_MIME_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'application/pdf',
  'text/csv',
  'application/csv',
  'application/vnd.ms-excel', // some browsers report .csv as this
  'text/plain',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
]);

/** Extension fallback: browsers often report an empty/odd MIME for csv/txt. */
const ALLOWED_EXTENSIONS = new Set([
  'png',
  'jpg',
  'jpeg',
  'webp',
  'gif',
  'pdf',
  'csv',
  'txt',
  'xlsx',
  'docx',
]);

export type AttachmentRejectReason = 'too_large' | 'unsupported_type';

export type AttachmentValidation =
  | { ok: true }
  | { ok: false; reason: AttachmentRejectReason };

const extensionOf = (name: string): string => {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
};

/**
 * Validate one staged file. Accepts when the MIME type is allowed OR the file
 * extension is allowed (lenient on purpose — the server re-checks). Size is
 * checked first so an oversized file reports `too_large` regardless of type.
 */
export const validateAttachmentFile = (file: File): AttachmentValidation => {
  if (file.size > MAX_ATTACHMENT_BYTES) {
    return { ok: false, reason: 'too_large' };
  }
  const typeAllowed =
    (!!file.type && ALLOWED_MIME_TYPES.has(file.type)) ||
    ALLOWED_EXTENSIONS.has(extensionOf(file.name));
  if (!typeAllowed) {
    return { ok: false, reason: 'unsupported_type' };
  }
  return { ok: true };
};
